import React, { useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { Loader, StepperControls, StepWrapper, StyledCardSection, TextField } from '@fnol/library/src/components';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    Radio,
    RadioGroup as MUIRadioGroup,
    Typography,
    ImageList,
    ImageListItem,
} from '@mui/material';
import { common, maxCharacterLimit, paymentOptions } from '@fnol/library/src/lib';
import { carImgData, translations } from '../../lib/consts';
import { damageOptions, question150MaxLength, question255MaxLength } from '../../lib/consts';
import './vehicle.css';

type GenericObject = { [key: string]: any };

type Props = {
    navigator: GenericObject[];
    values: GenericObject[];
};

const VehicleScreen = (props: Props) => {
    const { navigator } = props;
    const { values, submitForm, setFieldValue, touched, errors, setTouched, setErrors, isSubmitting } =
        useFormikContext<any>();
    const [radioSelected, setSelectedRadio] = useState<any>();
    const [images, setImages] = useState<GenericObject[]>([]);
    const [isSelected, setIsSelected] = useState<any>();

    const [currentStep, { goForward, goBack }] = navigator;

    const handleNext = (): void => {
        let valid = true;
        const existingErrors: GenericObject = {};
        setTouched(
            {
                vehicleMovable: true,
                vehicleLocation: true,
                vehicleDamages: true,
            },
            false,
        );
        if (!values.vehicleMovable || values.vehicleMovable === '') {
            existingErrors.vehicleMovable = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        } else {
            if (values.vehicleMovable === 'false' && (!values.vehicleLocation || values.vehicleLocation === '')) {
                existingErrors.vehicleLocation = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
        }
        if (
            values.vehicleTypeID === 1 &&
            !values.damages.find((item: GenericObject) => item.active === 'true') &&
            (JSON.stringify(values.vehicleDamage) === '[]' || values.vehicleDamage === undefined)
        ) {
            existingErrors.vehicleDamages = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (values.vehicleTypeID !== 1 && (!values.declaration || values.declaration === '')) {
            existingErrors.declaration = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        setErrors(existingErrors);
        if (valid) {
            submitForm();
        }
    };

    const handleGoBack = (): void => {
        goBack();
    };
    const handleChange = (e: any) => {
        setSelectedRadio(e.target.value);
    };

    const onChange = (e: any) => {
        if (e.target.checked) {
            if (!isSelected) {
                setFieldValue('payment', e.target.name);
                setIsSelected(e.target.name);
            }
        } else {
            setFieldValue('payment', '');
            setIsSelected(null);
        }
    };

    const RadioGroup = (params: any) => {
        return (
            <MUIRadioGroup row {...params}>
                <FormControlLabel
                    value={true}
                    onChange={handleChange}
                    control={<Radio />}
                    label={translations.vehicle.YES}
                />
                <FormControlLabel
                    value={false}
                    onChange={handleChange}
                    control={<Radio />}
                    label={translations.vehicle.NO}
                />
            </MUIRadioGroup>
        );
    };

    const handleImgClick = (e: any) => {
        const imageTitle = e.target.getAttribute('title');
        const newImages = images;
        const foundIndex = images.findIndex((x: any) => x.title == imageTitle);
        const newImage = newImages[foundIndex];
        if (newImage.active !== 'impossible') {
            newImage.active === 'false' ? (newImage.active = 'true') : (newImage.active = 'false');
        }
        newImages[foundIndex] = newImage;
        setFieldValue('damages', newImages);
        setImages(newImages);
    };

    const CarImageList = () => {
        return (
            <ImageList cols={5}>
                {images.map((item: any, index: any) => (
                    <ImageListItem key={item.img !== '' ? item.img : index}>
                        <img
                            src={item.img}
                            srcSet={`${item.img}?w=164&h=164&fit=crop&auto=format&dpr=2 2x`}
                            alt={item.title}
                            loading="lazy"
                            onClick={handleImgClick}
                            title={item.title}
                            style={{
                                backgroundColor: item.active === 'true' ? 'lightgray' : '',
                                cursor: item.active !== 'impossible' ? 'pointer' : 'default',
                            }}
                        />
                    </ImageListItem>
                ))}
            </ImageList>
        );
    };

    useEffect(() => {
        setImages(carImgData);
    }, [images]);

    useEffect(() => {
        if (values.submittedSuccessfully) {
            goForward();
        }
    }, [values.submittedSuccessfully]);

    useEffect(() => {
        if (values.vehicleMovable && values.vehicleMovable === 'false') {
            setSelectedRadio('false');
        }
    }, []);

    useEffect(() => {
        if (values.vehicleMovable === 'true') {
            setFieldValue('vehicleLocation', '');
        }
    }, [values.vehicleMovable]);

    return (
        <StepWrapper>
            <Loader openLoader={isSubmitting}></Loader>
            <StyledCardSection>
                <Typography style={{ fontWeight: 'bold', fontSize: '18px' }} variant="caption">
                    {translations.vehicle.DAMAGES}
                </Typography>

                <FormControl>
                    <Typography style={{ fontSize: '16px' }} variant="caption">
                        {translations.vehicle.VEHICLE_DRIVABLE}
                    </Typography>

                    <Field name="vehicleMovable" as={RadioGroup} />
                    {touched.vehicleMovable && errors.vehicleMovable ? (
                        <FormHelperText error={true}>
                            <>{errors.vehicleMovable}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
                <>
                    {radioSelected === 'false' ? (
                        <>
                            <Typography style={{ fontSize: '16px' }} variant="caption">
                                {translations.vehicle.VEHICLE_LOCATION}
                            </Typography>
                            <Field
                                name="vehicleLocation"
                                label={translations.vehicle.PLACE_AND_ADRESS}
                                as={TextField}
                                inputProps={{
                                    maxLength: question255MaxLength,
                                }}
                            />
                            {touched.vehicleLocation && errors.vehicleLocation ? (
                                <FormHelperText error={true}>
                                    <>{errors.vehicleLocation}</>
                                </FormHelperText>
                            ) : null}
                        </>
                    ) : (
                        <></>
                    )}
                </>
                {values.vehicleTypeID === 1 && (
                    <>
                        <Typography style={{ fontSize: '16px' }} variant="caption">
                            {translations.vehicle.DAMAGE_ZONE}
                        </Typography>
                        <Typography style={{ fontSize: '13px' }} variant="caption">
                            {translations.vehicle.DAMAGE_INFO}
                        </Typography>
                        <FormControl>
                            {touched.vehicleDamages && errors.vehicleDamages ? (
                                <FormHelperText error={true}>
                                    <>{errors.vehicleDamages}</>
                                </FormHelperText>
                            ) : null}
                            <div className="wrapper">
                                <div className="image-list-container">
                                    <Field name="damages" component={CarImageList} />
                                </div>
                                <div className="radio-wrapper">
                                    <FormControl component="fieldset" style={{ display: 'flex' }}>
                                        <FormGroup>
                                            {damageOptions.map((opt) => (
                                                <label key={opt.value} className="damage-label">
                                                    <Field
                                                        type="checkbox"
                                                        name="vehicleDamage"
                                                        value={opt.value}
                                                        className="checkbox"
                                                    />
                                                    {opt.label}
                                                </label>
                                            ))}
                                        </FormGroup>
                                    </FormControl>
                                </div>
                            </div>
                        </FormControl>
                    </>
                )}
                <Field
                    name="declaration"
                    multiline={true}
                    label={`${translations.vehicle.DAMAGE_DECLARATION} ${values.vehicleTypeID !== 1 ? '*' : ''}`}
                    as={TextField}
                    maxRows={2}
                    minRows={2}
                    inputProps={{
                        maxLength: maxCharacterLimit,
                    }}
                    value={values.declaration}
                    helperText={`Preostalo karaktera ${maxCharacterLimit - values.declaration.length}`}
                />
                <FormHelperText error={true}>
                    <>{errors.declaration}</>
                </FormHelperText>
            </StyledCardSection>
            <StyledCardSection>
                <Typography style={{ fontWeight: 'bold', fontSize: '18px' }} variant="caption">
                    {translations.vehicle.REPAIR_SERVICE}
                </Typography>
                <Typography style={{ fontSize: '16px' }} variant="caption">
                    {translations.vehicle.SERVICE_NAME}
                </Typography>
                <Field
                    name="serviceAdress"
                    label={translations.vehicle.NAME_AND_LOCATION}
                    as={TextField}
                    inputProps={{
                        maxLength: question150MaxLength,
                    }}
                />
            </StyledCardSection>
            <StyledCardSection>
                <Typography style={{ fontWeight: 'bold', fontSize: '18px' }} variant="caption">
                    {translations.vehicle.PAYMENT}
                </Typography>

                <FormControl component="fieldset" style={{ display: 'flex' }}>
                    <Typography style={{ fontSize: '16px' }} variant="caption">
                        {translations.vehicle.PAYMENT_RECEIVER}
                    </Typography>

                    <FormGroup>
                        {paymentOptions.map((opt: any) => {
                            return (
                                <label key={opt.value} className="damage-label">
                                    <Checkbox
                                        name={opt.value}
                                        key={opt.label}
                                        checked={values.payment == opt.value ? true : false}
                                        className="checkbox"
                                        disabled={isSelected ? isSelected != opt.value : false}
                                        onChange={onChange}
                                    />
                                    {opt.label}
                                </label>
                            );
                        })}
                    </FormGroup>
                </FormControl>
            </StyledCardSection>
            <StepperControls onPrevious={handleGoBack} onNext={handleNext} onNextLabel={common.NEXT} />
        </StepWrapper>
    );
};

export default VehicleScreen;
